<script setup lang="ts">
import IconPicker from 'ui/src/components/icons/IconPicker.vue'
import { useSiteLanguages } from '~/composables/useSiteLanguages'

const { locale } = useI18n()

const site = useSiteStore()
const menus = useMenusStore()
const { languages } = useSiteLanguages()

const currentYear = new Date().getFullYear()

const links = ref({
  socialLinks: [
    {
      name: 'TripAdvisor',
      url: site.data?.tripAdvisorLink,
      icon: 'trip-advisor'
    },
    {
      name: 'Facebook',
      url: site.data?.facebookLink,
      icon: 'facebook'
    },
    {
      name: 'Instagram',
      url: site.data?.instagramLink,
      icon: 'instagram'
    },
    {
      name: 'TikTok',
      url: site.data?.tiktokLink,
      icon: 'tiktok'
    },
    {
      name: 'YouTube',
      url: site.data?.youtubeLink,
      icon: 'youtube'
    }
  ],
  trustPilot: {
    url: site.data?.trustPilotLink
  }
})


const socialLinkAvailableCount = links.value.socialLinks.filter(link => link.url != null).length;
</script>

<template>
  <footer class="app-footer">
    <AppContainer class="app-footer__content">
      <div class="row logo-wrapper">
        <div class="col-lg-4 text-center lg:text-left">
          <NuxtLink href="/" class="app-footer__logo">
            <img
              :src="`/images/logos/${locale}.svg`"
              :alt="site.data?.name"
              class="app-logo hidden md:block"
            />
            <img
              :src="`/images/logos/mobile-${locale}.svg`"
              :alt="site.data?.name"
              class="app-logo md:hidden"
            />
          </NuxtLink>
        </div>
        <div class="useful-links">
          <div v-if="socialLinkAvailableCount > 0" class="social-links">
            <div v-for="(link, index) in links.socialLinks" :key="index">
              <div v-if="link.url" class="social-links__item">
                <a :href="link.url" target="_blank" class="social-link__item-url">
                  <IconPicker :name="link.icon"></IconPicker>
                </a>
              </div>
            </div>
          </div>
          <div v-if="links.trustPilot.url" class="trust-pilot-logo">
            <a :href="links.trustPilot.url" target="_blank">
              <IconTrustPilotLogo></IconTrustPilotLogo>
            </a>
          </div>
        </div>

        <div class="col-lg col-xl-4 hidden lg:block">
          <ul
            v-if="menus.footerPrimary?.length"
            class="app-footer__links text-button !mb-6 xl:!mb-0"
          >
            <li v-for="item in menus.footerPrimaryMenu" :key="item.id">
              <NuxtLink
                :to="item.url"
                :external="item.external"
                :target="item.external ? '_blank' : undefined"
                :rel="item.external ? 'noopener noreferrer' : undefined"
              >
                {{ item.title }}
              </NuxtLink>
            </li>
          </ul>
        </div>

        <div class="col-lg col-xl-2">
          <div
            class="app-footer__contacts text-primary-nav lg:text-secondary-nav"
          >
            <a
              v-if="site.data?.emailAddress"
              :href="`mailto:${site.data.emailAddress}`"
              class="max-w-full truncate"
              :title="site.data.emailAddress"
            >
              {{ site.data.emailAddress }}
            </a>

            <a
              v-if="site.data?.phoneNumber"
              :href="`tel:${site.data.phoneNumber.replaceAll(' ', '')}`"
            >
              {{ site.data.phoneNumber }}
            </a>
          </div>

          <address
            v-if="site.data?.address"
            class="app-footer__address md:text-secondary-nav text-sm"
          >
            {{ site.data.address }}
          </address>
        </div>
        <div class="col-lg col-xl-2 hidden lg:block">
          <ul class="app-footer__country-list">
            <li v-for="language in languages" :key="language.hash">
              <NuxtLink
                :to="language.link"
                class="text-secondary-nav"
              >
                <img
                  :src="`/images/flags/${language.code}.png`"
                  :alt="language.name"
                />

                <span>{{ language.name }}</span>
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
      <AppCountrySelect class="lg:hidden" />
    </AppContainer>

    <AppContainer class="app-footer__copyright" full-width>
      <ul v-if="menus.footerSecondary?.length" class="text-xs">
        <li v-for="item in menus.footerSecondaryMenu" :key="item.id">
          <NuxtLink
            :to="item.url"
            :external="item.external"
            :target="item.external ? '_blank' : undefined"
            :rel="item.external ? 'noopener noreferrer' : undefined"
          >
            {{ item.title }}
          </NuxtLink>
        </li>
      </ul>

      <p class="text-xs md:mr-auto">
        &copy; {{ currentYear }} {{ site.data?.name }}.
        {{ $t('string.All-rights-reserved') }}
      </p>
    </AppContainer>
  </footer>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-footer {
  width: 100%;
  color: $white;
  background-color: $black;
}

.useful-links {
  position: absolute;
  top: 5.625rem;
}

@include media-breakpoint-down(lg) {
  .useful-links {
    position: relative;
    top: 0;
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    justify-content: center;

    .social-links, .trust-pilot-logo {
      max-width: 100%;
      justify-items: center;
      align-items: center;
    }
  }
}

.logo-wrapper {
  position: relative;
}

.social-links, .trust-pilot-logo {
  max-width: 300px;
  display: flex;
  margin-bottom: 1rem;
}

.social-links__item {
  padding: 10px;
}

.social-links__item:hover {
  background-image: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.176));
  color: rgb(255, 255, 255);
}

.social-link__item-url {
  cursor: pointer;
}

.app-footer__content {
  padding-top: 2rem;
  padding-bottom: 2rem;

  @include media-breakpoint-up(lg) {
    padding-top: 3.25rem;
    padding-bottom: 3.25rem;
  }
}

.trust-pilot-logo > a {
  height: 2.625rem;
}

.app-footer__logo {
  display: inline-flex;
  height: 3.75rem;
  margin-bottom: 2rem;

  .app-logo {
    width: auto;
    height: 100%;
  }

  @include media-breakpoint-up(lg) {
    height: 4.625rem;
    margin-bottom: 0;
  }
}

.app-footer__links {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.app-footer__contacts {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 2rem;

  a + a {
    margin-top: 1rem;
  }

  @include media-breakpoint-up(lg) {
    align-items: flex-start;
    text-align: left;

    a + a {
      margin-top: 0.5rem;
    }
  }
}

.app-footer__address {
  margin-bottom: 2rem;
  text-align: center;

  @include media-breakpoint-up(lg) {
    text-align: left;
  }
}

.ui-country-select {
  width: 100%;
}

.app-footer__country-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    &:last-child {
      margin-bottom: 0;
    }

    img {
      flex-shrink: 0;
      width: 2rem;
      height: 2rem;
      margin-right: 0.625rem;
      border-radius: 50%;
      object-fit: cover;
    }
  }
}

.app-footer__copyright {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
  text-align: center;
  border-top: 1px solid $gray-400;

  ul {
    display: flex;
    margin: 0 0 0.75rem;
    padding: 0;
    list-style: none;

    li {
      &::after {
        content: '|';
        margin: 0 0.75rem;
        color: $gray-300;
        user-select: none;
        pointer-events: none;
      }

      &:last-child {
        &::after {
          content: none;
          display: none;
        }
      }
    }
  }

  @include media-breakpoint-up(md) {
    flex-direction: row-reverse;
    justify-content: space-between;

    ul {
      margin-bottom: 0;
    }
  }
}
</style>
